import { useCallback, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import {
  DetectedWallet,
  useWalletSign,
} from 'src/pages/admin/resources/xpx-perps-withdrawal/useWalletSign';

import { errorMessage, isUserRejection } from './helpers';

export type WalletSigner = {
  detectedWallets: DetectedWallet[];
  selectedWallet: DetectedWallet | null;
  selectWallet(name: string): void;
  connect(): Promise<void>;
  connecting: boolean;
  /** null until a wallet is connected — every action button gates on this. */
  address: string | null;
  signMessage(message: string): Promise<string>;
};

/**
 * All three rebalance actions are signed with the admin's web3 wallet, so wallet selection and
 * connection state live here instead of being copy-pasted into each dialog.
 */
export const useWalletSigner = (): WalletSigner => {
  const { detectedWallets, connect, address, signMessage } = useWalletSign();
  const [selectedWallet, setSelectedWallet] = useState<DetectedWallet | null>(null);
  const [connecting, setConnecting] = useState(false);

  const selectWallet = useCallback(
    (name: string) => {
      setSelectedWallet(detectedWallets.find((wallet) => wallet.name === name) ?? null);
    },
    [detectedWallets]
  );

  const handleConnect = useCallback(async () => {
    if (!selectedWallet) return;
    setConnecting(true);
    try {
      await connect(selectedWallet.provider);
    } finally {
      setConnecting(false);
    }
  }, [connect, selectedWallet]);

  return {
    detectedWallets,
    selectedWallet,
    selectWallet,
    connect: handleConnect,
    connecting,
    address,
    signMessage,
  };
};

type WalletSignerPickerProps = {
  signer: WalletSigner;
  /** True while the dialog's own submit is in flight. */
  busy?: boolean;
};

export const WalletSignerPicker = ({ signer, busy = false }: WalletSignerPickerProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const { detectedWallets, selectedWallet, selectWallet, connect, connecting, address } = signer;

  const handleConnect = async () => {
    try {
      await connect();
    } catch (error) {
      // Dismissing the wallet popup is not an error worth notifying about.
      if (isUserRejection(error)) return;
      notify(errorMessage(error, t('module.xbitRelayHotWalletBalancer.connectFailed')), {
        type: 'error',
      });
    }
  };

  if (detectedWallets.length === 0) {
    return (
      <Typography variant="body2" color="error">
        {t('module.xbitRelayHotWalletBalancer.noWalletFound')}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <FormControl size="small" fullWidth>
        <InputLabel>{t('module.xbitRelayHotWalletBalancer.selectWallet')}</InputLabel>
        <Select
          label={t('module.xbitRelayHotWalletBalancer.selectWallet')}
          value={selectedWallet?.name ?? ''}
          onChange={(event) => selectWallet(String(event.target.value))}
          disabled={Boolean(address) || connecting || busy}
        >
          {detectedWallets.map((wallet) => (
            <MenuItem key={wallet.name} value={wallet.name}>
              {wallet.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!address && selectedWallet && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleConnect}
          disabled={connecting || busy}
          startIcon={connecting ? <CircularProgress size={14} /> : null}
        >
          {t('module.xbitRelayHotWalletBalancer.connect')}
        </Button>
      )}

      {address ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
          {t('module.xbitRelayHotWalletBalancer.connectedAs')}: {address}
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ color: 'warning.main' }}>
          {t('module.xbitRelayHotWalletBalancer.walletRequired')}
        </Typography>
      )}
    </Stack>
  );
};
